import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  MinusCircle,
  Clock,
} from "lucide-react";
import { useHealth, type ServiceCheck } from "@/hooks/useHealth";
import { cn } from "@/lib/utils";

const SERVICE_LABELS: Record<string, string> = {
  database: "PostgreSQL Database",
  redis: "Redis Cache / Broker",
  celery_worker: "Celery Worker",
  celery_queues: "Celery Queue Depths",
  email_smtp: "Email (SMTP / SendGrid)",
  jira: "Jira API",
  jsm: "Jira Service Management",
  azure_storage: "Azure Blob Storage",
  key_vault: "Azure Key Vault",
};

const SERVICE_ORDER = [
  "database",
  "redis",
  "celery_worker",
  "celery_queues",
  "email_smtp",
  "jira",
  "jsm",
  "azure_storage",
  "key_vault",
];

const REQUIRED = new Set(["database", "redis", "email_smtp", "jira"]);

function StatusIcon({ status }: { status: ServiceCheck["status"] }) {
  if (status === "ok")
    return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
  if (status === "error") return <XCircle className="h-5 w-5 text-red-500" />;
  if (status === "degraded")
    return <AlertTriangle className="h-5 w-5 text-amber-500" />;
  if (status === "not_configured")
    return <MinusCircle className="h-5 w-5 text-gray-400" />;
  return <MinusCircle className="h-5 w-5 text-gray-400" />;
}

function StatusBadge({ status }: { status: ServiceCheck["status"] }) {
  const styles = {
    ok: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800",
    degraded:
      "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800",
    error:
      "bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800",
    unknown:
      "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600",
    not_configured:
      "bg-gray-50 dark:bg-gray-900 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700",
  } satisfies Record<ServiceCheck["status"], string>;

  const labels = {
    ok: "OK",
    degraded: "Degraded",
    error: "Error",
    unknown: "Unknown",
    not_configured: "Not Configured",
  } satisfies Record<ServiceCheck["status"], string>;

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
        styles[status],
      )}
    >
      {labels[status]}
    </span>
  );
}

function OverallBanner({ overall }: { overall: "ok" | "degraded" | "error" }) {
  if (overall === "ok")
    return (
      <div className="flex items-center gap-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 px-4 py-3">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <div>
          <p className="font-semibold text-emerald-800 dark:text-emerald-300">
            All systems operational
          </p>
          <p className="text-sm text-emerald-600 dark:text-emerald-400">
            All required services are running normally.
          </p>
        </div>
      </div>
    );
  if (overall === "degraded")
    return (
      <div className="flex items-center gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-4 py-3">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div>
          <p className="font-semibold text-amber-800 dark:text-amber-300">
            Partial degradation
          </p>
          <p className="text-sm text-amber-600 dark:text-amber-400">
            One or more required services are degraded.
          </p>
        </div>
      </div>
    );
  return (
    <div className="flex items-center gap-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 px-4 py-3">
      <XCircle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
      <div>
        <p className="font-semibold text-red-800 dark:text-red-300">
          System error
        </p>
        <p className="text-sm text-red-600 dark:text-red-400">
          One or more required services are unavailable.
        </p>
      </div>
    </div>
  );
}

export function HealthPage() {
  const { data, isLoading, isError, dataUpdatedAt, refetch, isFetching } =
    useHealth();

  const lastChecked = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  const orderedServices = SERVICE_ORDER.filter(
    (key) => data?.checks[key] !== undefined,
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">System Health</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Live status of all backend services and workers
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 rounded-md border bg-card px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent disabled:opacity-50"
        >
          <RefreshCw
            className={cn("h-3.5 w-3.5", isFetching && "animate-spin")}
          />
          Refresh
        </button>
      </div>

      {/* Overall status */}
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Checking services…
        </div>
      )}
      {isError && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 px-4 py-3">
          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <p className="text-sm font-medium text-red-700 dark:text-red-300">
            Failed to load health status. You may not have permission or the API
            is unreachable.
          </p>
        </div>
      )}
      {data && <OverallBanner overall={data.overall} />}

      {/* Service grid */}
      {data && (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Service
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Status
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Latency
                </th>
                <th className="hidden px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:table-cell">
                  Detail
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {orderedServices.map((key) => {
                const check = data.checks[key];
                const label = SERVICE_LABELS[key] ?? key;
                const required = REQUIRED.has(key);
                return (
                  <tr key={key} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <StatusIcon status={check.status} />
                        <span className="font-medium text-foreground">
                          {label}
                        </span>
                        {!required && (
                          <span className="text-xs text-muted-foreground">
                            (optional)
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={check.status} />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                      {check.latency_ms > 0
                        ? `${check.latency_ms.toFixed(0)} ms`
                        : "—"}
                    </td>
                    <td className="hidden px-4 py-3 text-muted-foreground sm:table-cell">
                      <span className="line-clamp-1" title={check.detail}>
                        {check.detail}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      {lastChecked && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          Last checked at {lastChecked} · Auto-refreshes every 30 s
        </div>
      )}
    </div>
  );
}
